import { useQueryClient } from '@tanstack/react-query';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { Nullable } from '@gov-nx/core/types/helpers.types';
import { useAxiosInstance } from '../axios';
import { logger } from '../logger';
import {
	fetchSettingsQuery,
	saveSettingsQuery,
} from '../queries/settingsQuery';
import type { DatovkaNastaveniArchivaceValue } from '../types/settings';
import { usePoMutation } from './usePoMutation';
import { usePoQuery } from './usePoQuery';

interface ArchivingSettingsVariables {
	value: Nullable<string>;
}

export const useArchivingSettingsQuery = (dataBoxId?: string) => {
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const queryClient = useQueryClient();

	const key = `AA_DS_${dataBoxId}`;
	const queryKey = ['setting-archiving', key];

	const query = usePoQuery({
		queryKey,
		enabled: !!dataBoxId,
		queryFn: () => {
			logger('useArchivingSettingsQuery -> fetchSettingsQuery', []);
			return fetchSettingsQuery<DatovkaNastaveniArchivaceValue>(
				axiosInstance,
				key
			);
		},
		refetchOnWindowFocus: false,
	});
	const { axiosInstance } = useAxiosInstance();
	const mutation = usePoMutation({
		mutationFn: (variables: ArchivingSettingsVariables) =>
			saveSettingsQuery(axiosInstance, {
				klic: key,
				hodnota: variables.value,
			}),
		onSuccess: (response, variables) => {
			queryClient.setQueryData(queryKey, {
				klic: key,
				hodnota: variables.value,
			});
			toastMessageSuccess(t('ulozeno'));
		},
		onError: () => {
			toastMessageError(t('chyba-ulozeni'));
		},
	});

	return {
		isLoading: query.isLoading,
		data: query.data,

		mutateAsync: mutation.mutateAsync,
		mutate: mutation.mutate,
		isMutating: mutation.isLoading,
	};
};

interface NotificationSettingsVariables {
	value: Nullable<string>;
}

export const useNotificationSettingsQuery = (dataBoxId?: string) => {
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();

	const key = createNotificationSettingsKey(dataBoxId);
	const queryKey = createNotificationSettingsQueryKey(key);

	const query = usePoQuery({
		queryKey,
		enabled: !!dataBoxId,
		queryFn: () => {
			logger('useNotificationSettingsQuery -> fetchSettingsQuery', []);
			return fetchSettingsQuery<string>(axiosInstance, key);
		},
		refetchOnWindowFocus: false,
	});

	const mutation = usePoMutation({
		mutationFn: (variables: NotificationSettingsVariables) =>
			saveSettingsQuery(axiosInstance, {
				klic: key,
				hodnota: variables.value,
			}),
		onSuccess: (response, variables) => {
			queryClient.setQueryData(queryKey, {
				klic: key,
				hodnota: variables.value,
			});
			toastMessageSuccess(t('ulozeno'));
		},
		onError: () => {
			toastMessageError(t('chyba-ulozeni'));
		},
	});

	return {
		isLoading: query.isLoading,
		isSuccess: query.isSuccess,
		data: query.data,
		mutateAsync: mutation.mutateAsync,
		mutate: mutation.mutate,
		isMutating: mutation.isLoading,
		invalidate: async (dataBoxId: string) => {
			const key = createNotificationSettingsKey(dataBoxId);
			const queryKey = createNotificationSettingsQueryKey(key);
			return queryClient.invalidateQueries({ queryKey });
		},
	};
};

const createNotificationSettingsKey = (dataBoxId?: string) =>
	`N_DS_${dataBoxId}`;

const createNotificationSettingsQueryKey = (key: string) => [
	'setting-notifications',
	key,
];
